/**
 * TalentLens AI - Zod Validation Schemas
 * Comprehensive validation schemas for all API inputs and outputs
 */

import { z } from 'zod';
import {
  USER_ROLES,
  CV_STATUS,
  JOB_PRIORITY,
  ALLOWED_FILE_TYPES,
  MAX_FILE_SIZE,
  PAGINATION,
  EXPORT_FORMATS,
  PIPELINE_STAGES,
  INTERVIEW_TYPES,
  INTERVIEW_STATUS,
  INTERVIEW_RECOMMENDATIONS,
  EMAIL_TEMPLATE_TYPES,
  EMAIL_STATUS,
} from '../constants/index.js';

// ============================================================================
// User Schemas
// ============================================================================

export const userRoleSchema = z.enum([
  USER_ROLES.ADMIN,
  USER_ROLES.RECRUITER,
  USER_ROLES.HIRING_MANAGER,
  USER_ROLES.VIEWER,
]);

export const userSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      'Password must contain uppercase, lowercase, and number'
    ),
  name: z.string().min(2, 'Name must be at least 2 characters'),
  role: userRoleSchema,
});

export const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

export const updateUserSchema = userSchema.partial().omit({ password: true });

export const changePasswordSchema = z.object({
  current_password: z.string().min(1, 'Current password is required'),
  new_password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      'Password must contain uppercase, lowercase, and number'
    ),
});

// ============================================================================
// CV Schemas
// ============================================================================

export const cvStatusSchema = z.enum([
  CV_STATUS.PENDING,
  CV_STATUS.PROCESSING,
  CV_STATUS.PROCESSED,
  CV_STATUS.FAILED,
  CV_STATUS.PARTIAL,
]);

export const cvSkillSchema = z.object({
  skill: z.string().min(1, 'Skill name is required'),
  years: z.number().min(0, 'Years must be non-negative').max(50, 'Years cannot exceed 50'),
});

export const cvExperienceSchema = z.object({
  title: z.string().min(1, 'Job title is required'),
  company: z.string().min(1, 'Company name is required'),
  start_date: z.coerce.date(),
  end_date: z.coerce.date().optional(),
  description: z.string(),
  projects: z.array(z.string()).default([]),
});

export const cvEducationSchema = z.object({
  degree: z.string().min(1, 'Degree is required'),
  institution: z.string().min(1, 'Institution is required'),
  graduation_date: z.coerce.date(),
});

export const cvCertificationSchema = z.object({
  name: z.string().min(1, 'Certification name is required'),
  issuer: z.string().min(1, 'Issuer is required'),
  issue_date: z.coerce.date(),
  expiry_date: z.coerce.date().optional(),
});

export const parsedCVDataSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email().optional(),
  phone: z.string().optional(),
  skills: z.array(cvSkillSchema).min(1, 'At least one skill is required'),
  experience: z.array(cvExperienceSchema).default([]),
  education: z.array(cvEducationSchema).default([]),
  certifications: z.array(cvCertificationSchema).default([]),
});

export const cvUploadRequestSchema = z.object({
  filename: z.string().min(1, 'Filename is required'),
  mime_type: z.enum([ALLOWED_FILE_TYPES.PDF, ALLOWED_FILE_TYPES.DOCX], {
    errorMap: () => ({ message: 'Only PDF and DOCX files are allowed' }),
  }),
  size: z
    .number()
    .min(1, 'File size must be greater than 0')
    .max(MAX_FILE_SIZE, `File size cannot exceed ${MAX_FILE_SIZE / 1024 / 1024}MB`),
});

export const cvRegisterSchema = z.object({
  r2_object_key: z.string().min(1, 'Object key is required'),
  filename: z.string().min(1, 'Filename is required'),
  mime_type: z.string(),
  size: z.number().positive(),
});

export const cvFilterSchema = z.object({
  status: z.array(cvStatusSchema).optional(),
  uploaded_by: z.string().optional(),
  is_archived: z.boolean().optional(),
  date_from: z.coerce.date().optional(),
  date_to: z.coerce.date().optional(),
});

// ============================================================================
// Job Schemas
// ============================================================================

export const jobPrioritySchema = z.enum([
  JOB_PRIORITY.LOW,
  JOB_PRIORITY.MEDIUM,
  JOB_PRIORITY.HIGH,
]);

export const createJobSchema = z.object({
  title: z.string().min(3, 'Title must be at least 3 characters').max(200),
  description: z.string().min(50, 'Description must be at least 50 characters'),
  team: z.string().optional(),
  priority: jobPrioritySchema.default(JOB_PRIORITY.MEDIUM),
});

export const updateJobSchema = z.object({
  title: z.string().min(3).max(200).optional(),
  description: z.string().min(50).optional(),
  team: z.string().optional(),
  priority: jobPrioritySchema.optional(),
  changes: z.string().optional(),
});

export const jobFilterSchema = z.object({
  owner: z.string().optional(),
  team: z.string().optional(),
  priority: z.array(jobPrioritySchema).optional(),
  is_active: z.boolean().optional(),
  date_from: z.coerce.date().optional(),
  date_to: z.coerce.date().optional(),
});

// ============================================================================
// Search Schemas
// ============================================================================

export const searchFiltersSchema = z.object({
  skills: z.array(z.string()).optional(),
  years_experience: z
    .object({
      min: z.number().min(0).optional(),
      max: z.number().min(0).optional(),
    })
    .optional(),
  education_level: z.string().optional(),
  keywords: z.string().optional(),
  role_level: z.string().optional(),
  status: z.array(cvStatusSchema).optional(),
  uploaded_by: z.string().optional(),
  date_range: z
    .object({
      start: z.coerce.date().optional(),
      end: z.coerce.date().optional(),
    })
    .optional(),
});

export const searchQuerySchema = z.object({
  query: z.string().min(1, 'Query cannot be empty'),
  filters: searchFiltersSchema.optional(),
  semantic: z.boolean().default(false),
  limit: z
    .number()
    .min(1)
    .max(PAGINATION.MAX_PAGE_SIZE)
    .default(PAGINATION.DEFAULT_PAGE_SIZE),
  cursor: z.string().optional(),
});

export const autocompleteQuerySchema = z.object({
  query: z.string().min(1, 'Query cannot be empty'),
  field: z.enum(['skills', 'companies', 'titles']),
  limit: z.number().min(1).max(20).default(10),
});

// ============================================================================
// Matching Schemas
// ============================================================================

export const matchRequestSchema = z.object({
  job_id: z.string().min(1, 'Job ID is required'),
  filters: searchFiltersSchema.optional(),
  limit: z
    .number()
    .min(1)
    .max(PAGINATION.MAX_PAGE_SIZE)
    .default(PAGINATION.DEFAULT_PAGE_SIZE),
  cursor: z.string().optional(),
  explain: z.boolean().default(false),
});

export const matchExplanationSchema = z.object({
  skills_match: z.object({
    score: z.number().min(0).max(100),
    matched: z.array(z.string()),
    missing: z.array(z.string()),
  }),
  experience_match: z.object({
    score: z.number().min(0).max(100),
    summary: z.string(),
  }),
  education_match: z.object({
    score: z.number().min(0).max(100),
    summary: z.string(),
  }),
  highlights: z.array(z.string()),
});

// ============================================================================
// Export Schemas
// ============================================================================

export const exportFormatSchema = z.enum([
  EXPORT_FORMATS.CSV,
  EXPORT_FORMATS.PDF,
  EXPORT_FORMATS.EXCEL,
]);

export const exportRequestSchema = z.object({
  format: exportFormatSchema,
  cv_ids: z.array(z.string()).optional(),
  search_query: searchQuerySchema.optional(),
});

// ============================================================================
// Analytics Schemas
// ============================================================================

export const analyticsRequestSchema = z.object({
  date_from: z.coerce.date().optional(),
  date_to: z.coerce.date().optional(),
  group_by: z.enum(['day', 'week', 'month']).default('day'),
});

// ============================================================================
// Pagination Schemas
// ============================================================================

export const paginationSchema = z.object({
  page: z.number().min(1).default(1),
  limit: z
    .number()
    .min(1)
    .max(PAGINATION.MAX_PAGE_SIZE)
    .default(PAGINATION.DEFAULT_PAGE_SIZE),
  cursor: z.string().optional(),
});

export const sortSchema = z.object({
  field: z.string(),
  order: z.enum(['asc', 'desc']).default('desc'),
});

// ============================================================================
// Audit Log Schemas
// ============================================================================

export const auditLogFilterSchema = z.object({
  user_id: z.string().optional(),
  action: z.string().optional(),
  resource_type: z.string().optional(),
  date_from: z.coerce.date().optional(),
  date_to: z.coerce.date().optional(),
});

// ============================================================================
// LLM Response Schemas (for structured output validation)
// ============================================================================

export const llmCVParseResponseSchema = parsedCVDataSchema;

export const llmQueryExpansionResponseSchema = z.object({
  filters: searchFiltersSchema,
  semantic_query: z.string(),
  role_level: z.string().optional(),
});

export const llmMatchExplanationResponseSchema = matchExplanationSchema;

// ============================================================================
// Validation Helpers
// ============================================================================

export const validateObjectId = (id: string): boolean => {
  return /^[0-9a-fA-F]{24}$/.test(id);
};

export const objectIdSchema = z.string().refine(validateObjectId, {
  message: 'Invalid ObjectId format',
});

// ============================================================================
// API Response Schemas
// ============================================================================

export const errorResponseSchema = z.object({
  type: z.string(),
  title: z.string(),
  status: z.number(),
  detail: z.string(),
  instance: z.string(),
});

export const successResponseSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({
    success: z.literal(true),
    data: dataSchema,
  });

export const errorResponseWrapperSchema = z.object({
  success: z.literal(false),
  error: errorResponseSchema,
});

// ============================================================================
// Batch Operations Schemas
// ============================================================================

export const batchDeleteSchema = z.object({
  ids: z.array(z.string()).min(1, 'At least one ID is required'),
});

export const batchArchiveSchema = z.object({
  ids: z.array(z.string()).min(1, 'At least one ID is required'),
  archive: z.boolean(),
});

// ============================================================================
// WebSocket Schemas
// ============================================================================

export const wsEventSchema = z.object({
  type: z.enum([
    'processing_started',
    'processing_progress',
    'processing_completed',
    'processing_failed',
  ]),
  cv_id: z.string(),
  status: cvStatusSchema,
  progress_percent: z.number().min(0).max(100).optional(),
  error: z.string().optional(),
  timestamp: z.coerce.date(),
});

// ============================================================================
// Type Inference Helpers
// ============================================================================

export type UserInput = z.infer<typeof userSchema>;
export type LoginInput = z.infer<typeof loginSchema>;
export type CVUploadRequest = z.infer<typeof cvUploadRequestSchema>;
export type CVRegisterRequest = z.infer<typeof cvRegisterSchema>;
export type CreateJobInput = z.infer<typeof createJobSchema>;
export type UpdateJobInput = z.infer<typeof updateJobSchema>;
export type SearchQueryInput = z.infer<typeof searchQuerySchema>;
export type MatchRequestInput = z.infer<typeof matchRequestSchema>;
export type ExportRequestInput = z.infer<typeof exportRequestSchema>;
export type ParsedCVDataInput = z.infer<typeof parsedCVDataSchema>;

// ============================================================================
// Schema Aliases for Route Compatibility
// ============================================================================

export const cvUploadSchema = z.object({
  objectKey: z.string().min(1, 'Object key is required'),
  filename: z.string().min(1, 'Filename is required'),
});

export const cvListQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().min(1).max(100).default(20),
  status: z.string().optional(),
  archived: z.string().optional(),
  sortBy: z.string().optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
});

export const jobCreateSchema = createJobSchema.extend({
  ownerId: z.string().optional(),
});

export const jobUpdateSchema = updateJobSchema.extend({
  ownerId: z.string().optional(),
  isActive: z.boolean().optional(),
});

// ============================================================================
// Pipeline Schemas
// ============================================================================

export const pipelineStageSchema = z.enum([
  PIPELINE_STAGES.APPLIED,
  PIPELINE_STAGES.PHONE_SCREEN,
  PIPELINE_STAGES.INTERVIEW,
  PIPELINE_STAGES.TECHNICAL,
  PIPELINE_STAGES.FINAL,
  PIPELINE_STAGES.OFFER,
  PIPELINE_STAGES.HIRED,
  PIPELINE_STAGES.REJECTED,
]);

export const addToPipelineSchema = z.object({
  job_id: z.string().min(1, 'Job ID is required'),
  cv_id: z.string().min(1, 'CV ID is required'),
  stage: pipelineStageSchema.default(PIPELINE_STAGES.APPLIED),
  notes: z.string().optional(),
  rating: z.number().min(1).max(5).optional(),
  tags: z.array(z.string()).optional(),
});

export const updatePipelineSchema = z.object({
  notes: z.string().optional(),
  rating: z.number().min(1).max(5).optional(),
  tags: z.array(z.string()).optional(),
});

export const updatePipelineStageSchema = z.object({
  stage: pipelineStageSchema,
  notes: z.string().optional(),
});

export const rejectCandidateSchema = z.object({
  reason: z.string().min(1, 'Rejection reason is required'),
});

// ============================================================================
// Interview Schemas
// ============================================================================

export const interviewTypeSchema = z.enum([
  INTERVIEW_TYPES.PHONE,
  INTERVIEW_TYPES.VIDEO,
  INTERVIEW_TYPES.ONSITE,
]);

export const interviewStatusSchema = z.enum([
  INTERVIEW_STATUS.SCHEDULED,
  INTERVIEW_STATUS.CONFIRMED,
  INTERVIEW_STATUS.COMPLETED,
  INTERVIEW_STATUS.CANCELLED,
  INTERVIEW_STATUS.NO_SHOW,
]);

export const interviewRecommendationSchema = z.enum([
  INTERVIEW_RECOMMENDATIONS.STRONG_YES,
  INTERVIEW_RECOMMENDATIONS.YES,
  INTERVIEW_RECOMMENDATIONS.NEUTRAL,
  INTERVIEW_RECOMMENDATIONS.NO,
  INTERVIEW_RECOMMENDATIONS.STRONG_NO,
]);

export const interviewerSchema = z.object({
  user_id: z.string().min(1),
  name: z.string().min(1),
  email: z.string().email(),
});

export const scheduleInterviewSchema = z.object({
  pipeline_id: z.string().min(1, 'Pipeline ID is required'),
  title: z.string().min(1, 'Interview title is required'),
  type: interviewTypeSchema,
  scheduled_at: z.coerce.date(),
  duration_minutes: z.number().min(15).max(480).default(60),
  location: z.string().optional(),
  meeting_link: z.string().url().optional().or(z.literal('')),
  interviewers: z.array(interviewerSchema).min(1, 'At least one interviewer is required'),
  send_invite: z.boolean().default(true),
});

export const updateInterviewSchema = z.object({
  title: z.string().min(1).optional(),
  type: interviewTypeSchema.optional(),
  scheduled_at: z.coerce.date().optional(),
  duration_minutes: z.number().min(15).max(480).optional(),
  location: z.string().optional(),
  meeting_link: z.string().url().optional().or(z.literal('')),
  interviewers: z.array(interviewerSchema).optional(),
  status: interviewStatusSchema.optional(),
});

export const interviewFeedbackSchema = z.object({
  rating: z.number().min(1).max(5),
  recommendation: interviewRecommendationSchema,
  strengths: z.string().min(1, 'Please provide strengths'),
  concerns: z.string().optional().default(''),
  notes: z.string().optional().default(''),
});

export const interviewListQuerySchema = z.object({
  job_id: z.string().optional(),
  status: interviewStatusSchema.optional(),
  from_date: z.coerce.date().optional(),
  to_date: z.coerce.date().optional(),
  interviewer_id: z.string().optional(),
  limit: z.coerce.number().min(1).max(100).default(20),
  offset: z.coerce.number().min(0).default(0),
});

// ============================================================================
// Email Schemas
// ============================================================================

export const emailTemplateTypeSchema = z.enum([
  EMAIL_TEMPLATE_TYPES.INTERVIEW_INVITE,
  EMAIL_TEMPLATE_TYPES.INTERVIEW_REMINDER,
  EMAIL_TEMPLATE_TYPES.INTERVIEW_FEEDBACK_REQUEST,
  EMAIL_TEMPLATE_TYPES.REJECTION,
  EMAIL_TEMPLATE_TYPES.OFFER,
  EMAIL_TEMPLATE_TYPES.CUSTOM,
]);

export const emailStatusSchema = z.enum([
  EMAIL_STATUS.SENT,
  EMAIL_STATUS.FAILED,
  EMAIL_STATUS.BOUNCED,
]);

export const createEmailTemplateSchema = z.object({
  name: z.string().min(1, 'Template name is required'),
  type: emailTemplateTypeSchema,
  subject: z.string().min(1, 'Subject is required'),
  body: z.string().min(1, 'Body is required'),
  is_default: z.boolean().default(false),
});

export const updateEmailTemplateSchema = createEmailTemplateSchema.partial();

export const sendEmailSchema = z.object({
  to: z.string().email('Valid email is required'),
  cc: z.array(z.string().email()).optional(),
  subject: z.string().min(1, 'Subject is required'),
  body: z.string().min(1, 'Body is required'),
  template_id: z.string().optional(),
  pipeline_id: z.string().optional(),
  interview_id: z.string().optional(),
});

export const previewEmailSchema = z.object({
  template_id: z.string().min(1, 'Template ID is required'),
  placeholders: z.record(z.string()).optional(),
});

// ============================================================================
// Pipeline/Interview Type Inference
// ============================================================================

export type AddToPipelineInput = z.infer<typeof addToPipelineSchema>;
export type UpdatePipelineInput = z.infer<typeof updatePipelineSchema>;
export type UpdatePipelineStageInput = z.infer<typeof updatePipelineStageSchema>;
export type RejectCandidateInput = z.infer<typeof rejectCandidateSchema>;
export type ScheduleInterviewInput = z.infer<typeof scheduleInterviewSchema>;
export type UpdateInterviewInput = z.infer<typeof updateInterviewSchema>;
export type InterviewFeedbackInput = z.infer<typeof interviewFeedbackSchema>;
export type CreateEmailTemplateInput = z.infer<typeof createEmailTemplateSchema>;
export type SendEmailInput = z.infer<typeof sendEmailSchema>;
